import { Box, Button, Stack } from '@mui/material';
import { AnalyticCard } from '../AnalyticCard/AnalyticCard';
import { DEFAULT_CURRENCY } from '@vy/types';
import { RevenueTrendsChart } from '../RevenueTrendsChart/RevenueTrendsChart';
import { useGetYtdBilledRevenue } from '../../../../../hooks/requests/use-get-ytd-billed-revenue';
import { useGetRecurringRevenue } from '../../../../../hooks/requests/use-get-recurring-revenue';
import { vyDate } from '@vy/utils';
import { useMemo } from 'react';
import { AnalyticCardEmptyState } from '../../EmptyState/AnalyticCardEmptyState';
import { getSelectorAttribute, homePageWidgetsSelectors } from '@vy/app-selectors';
import { Parallelepipeds } from '@vy/ui-design-system';
import { formatPriceToAbbreviated } from '../../../../../utils/formatters/formatPriceToAbbreviated';
import { Link } from 'react-router-dom';
import { AnalyticMetricV2 } from '../AnalyticMetric/AnalyticMetricV2.tsx';

export const RevenueTrendsAnalyticsCard = () => {
  const { data: ytdRevenue, isLoading: isYtdLoading } = useGetYtdBilledRevenue(undefined, {
    suspense: false,
  });
  const { data: recurringRevenue, isLoading: isRecRevenueLoading } = useGetRecurringRevenue(undefined, {
    suspense: false,
  });
  const { annualRecurringRevenue = 0, monthlyRecurringRevenue = 0 } = recurringRevenue ?? {};

  const { chartDataset, previousAnnualRecurringRevenue } = useMemo(() => {
    if (!recurringRevenue?.recurringRevenueTrend) {
      return {
        chartDataset: [],
        previousAnnualRecurringRevenue: undefined,
      };
    }

    const sortedTrends = [...recurringRevenue.recurringRevenueTrend].sort((firstTrend, secondTrend) => {
      const firstMonthTimestamp = vyDate(firstTrend.month).valueOf();
      const secondMonthTimestamp = vyDate(secondTrend.month).valueOf();

      return firstMonthTimestamp - secondMonthTimestamp;
    });

    const limitedTrends = sortedTrends.slice(-6);
    const previousTrend = limitedTrends[limitedTrends.length - 2];

    const dataset = limitedTrends.map((trendPoint) => {
      const monthDate = vyDate(trendPoint.month).startOf('month');

      return {
        month: monthDate.toDate(),
        mrr: trendPoint.monthlyRecurringRevenue,
        arr: trendPoint.monthlyRecurringRevenue * 12,
      };
    });

    return {
      chartDataset: dataset,
      previousAnnualRecurringRevenue: previousTrend ? previousTrend.monthlyRecurringRevenue * 12 : undefined,
    };
  }, [recurringRevenue?.recurringRevenueTrend]);

  const hasTrendData = chartDataset.length > 0;
  const isNoData = useMemo(() => {
    const hasNonZeroMetric = [
      ytdRevenue?.ytdBilledRevenue,
      annualRecurringRevenue,
      monthlyRecurringRevenue,
    ].some((value) => (value ?? 0) !== 0);

    return !hasTrendData && !hasNonZeroMetric;
  }, [hasTrendData, ytdRevenue?.ytdBilledRevenue, annualRecurringRevenue, monthlyRecurringRevenue]);

  return (
    <AnalyticCard
      title="Revenue Trends"
      dataVy={homePageWidgetsSelectors.revenueTrendsWidget}
      isLoading={isYtdLoading || isRecRevenueLoading}
      renderAdditionalHeaderContent={() => (
        <Button
          component={Link}
          to="/reports/recurring-revenue"
          variant="text"
          sx={{ color: '#0521FF' }}
          size="small"
          {...getSelectorAttribute(homePageWidgetsSelectors.revenueTrendsWidgetViewReport)}
        >
          View Report
        </Button>
      )}
    >
      {isNoData ? (
        <AnalyticCardEmptyState
          title="No revenue data yet"
          description="once you start invoicing, this space will come alive."
          icon={<Parallelepipeds />}
        />
      ) : (
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Box sx={{
            flex: 1
          }}>
            <AnalyticMetricV2
              titleText="ARR (Last 12 months)"
              value={annualRecurringRevenue}
              prevValue={previousAnnualRecurringRevenue}
              formatter={(value) => formatPriceToAbbreviated(DEFAULT_CURRENCY, value)}
              growthRateLabel="MoM"
              dataVy={homePageWidgetsSelectors.revenueTrendsWidgetArr}
            />
          </Box>

          <Box
            {...getSelectorAttribute(homePageWidgetsSelectors.revenueTrendsWidgetChart)}
            sx={{
              flex: 2,
              minWidth: 0
            }}>
            <RevenueTrendsChart dataset={chartDataset} chartHeight={200} />
          </Box>
        </Stack>
      )}
    </AnalyticCard>
  );
};
